import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useCart } from '@/context/CartContext';
import { ArrowLeft, Star, ShoppingBag, Scissors, Droplets } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOOD_FOR = {
  'daily-face-wash': ['Hard water', 'Daily use', 'Oily skin'],
  'daily-moisturiser-spf30': ['UV protection', 'All skin types', 'No white cast'],
  'pro-active-face-wash': ['Acne-prone', 'Deep clean', 'Post-shave bumps'],
  'pro-defence-moisturiser-spf50': ['Sun exposure', 'High UV days', 'Outdoor workers'],
};

const RATINGS = {
  'daily-face-wash': { score: 4.8, count: 2547 },
  'daily-moisturiser-spf30': { score: 4.7, count: 1823 },
  'pro-active-face-wash': { score: 4.9, count: 1456 },
  'pro-defence-moisturiser-spf50': { score: 4.9, count: 987 },
};

const ACTIVES = {
  'daily-face-wash': 'Niacinamide 3% · Panthenol · Allantoin',
  'daily-moisturiser-spf30': 'Niacinamide 4% · SPF 30 PA++',
  'pro-active-face-wash': 'Salicylic Acid 1.5% · Niacinamide 4.5% · Tea Tree',
  'pro-defence-moisturiser-spf50': 'Niacinamide 5% · Ceramide NP · SPF 50 PA+++',
};

export default function ShopPage() {
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    axios.get(`${API}/products`).then(r => setProducts(r.data)).catch(err => console.error('API request failed:', err));
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.hash]);

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 200);
    }
  }, [location.hash, products.length]);

  const face = products.filter(p => p.category === 'face');
  const daily = face.filter(p => p.tier === 'daily');
  const pro = face.filter(p => p.tier === 'pro');

  const dailyTotal = daily.reduce((s, p) => s + p.price, 0);
  const proTotal = pro.reduce((s, p) => s + p.price, 0);
  const addAll = (items) => items.forEach(p => addItem({ id: p.slug, name: p.name, price: p.price, size: p.size, image: p.image || '', quantity: 1 }));

  const typeOrder = (slug) => slug.includes('face-wash') ? 0 : 1;
  const dailySorted = [...daily].sort((a, b) => typeOrder(a.slug) - typeOrder(b.slug));
  const proSorted = [...pro].sort((a, b) => typeOrder(a.slug) - typeOrder(b.slug));

  return (
    <div className="bg-white">
      {/* Header */}
      <section className="bg-[#0C4A8C] py-10 md:py-14">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <button onClick={() => navigate(-1)} data-testid="back-button" className="inline-flex items-center gap-2 text-white/40 hover:text-white text-[12px] mb-6 transition-colors">
            <ArrowLeft size={13} /> Back
          </button>
          <p className="eyebrow mb-3 text-[#BAE6FD]">Shop</p>
          <h1 data-testid="shop-heading" className="text-3xl md:text-[44px] font-serif font-semibold text-white tracking-tight mb-3">
            Skin first. Four products. Zero fluff.
          </h1>
          <p className="text-[14px] text-white/50 font-light max-w-xl">
            pH-balanced, sulphate-free, no white cast — formulated for Indian hard water, humidity and UV. Hair &amp; body care coming soon.
          </p>
        </div>
      </section>

      {/* Daily | Pro */}
      <div className="px-2 md:px-4">
        <section className="bg-white max-w-5xl mx-auto mt-5 mb-5 rounded-[4px] py-10 md:py-14 px-6 md:px-10" style={{ boxShadow: '0 1px 3px rgba(12,74,140,0.06)', border: '1px solid rgba(12,74,140,0.06)' }}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10 mb-8">
            <div className="flex items-center justify-between" data-testid="daily-tier">
              <div>
                <p className="eyebrow text-[#0C7BC0] mb-1">TRUFORM Daily</p>
                <p className="text-[12px] text-[#6B7D8A] font-light">Clean, protected baseline.</p>
              </div>
              {daily.length > 1 && (
                <button data-testid="add-duo-daily" onClick={() => addAll(daily)}
                  className="bg-[#0C4A8C] text-white px-4 py-2 rounded-[2px] text-[10px] font-sans font-medium uppercase tracking-[2px] hover:bg-[#093B70] transition-colors whitespace-nowrap">
                  Add All &#8377;{dailyTotal}
                </button>
              )}
            </div>
            <div className="flex items-center justify-between" data-testid="pro-tier">
              <div>
                <p className="eyebrow mb-1" style={{ color: '#0C7BC0' }}>TRUFORM Pro</p>
                <p className="text-[12px] text-[#6B7D8A] font-light">Active ingredients. Visible results.</p>
              </div>
              {pro.length > 1 && (
                <button data-testid="add-duo-pro" onClick={() => addAll(pro)}
                  className="text-white px-4 py-2 rounded-[2px] text-[10px] font-sans font-medium uppercase tracking-[2px] hover:opacity-90 transition-colors whitespace-nowrap bg-[#0C7BC0]">
                  Add All &#8377;{proTotal}
                </button>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-5">
            {dailySorted.map((dp, i) => {
              const pp = proSorted[i];
              return (
                <div key={dp.slug} className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-10">
                  <ProductCard product={dp} variant="daily" onAdd={addItem} />
                  {pp ? <ProductCard product={pp} variant="pro" onAdd={addItem} /> : <div />}
                </div>
              );
            })}
          </div>
        </section>
      </div>

      {/* Coming Soon — Hair & Body */}
      <ComingSoonSection />
    </div>
  );
}

function ComingSoonSection() {
  const [email, setEmail] = useState('');
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/waitlist`, { email, source: 'shop-coming-soon' });
      setDone(true);
      setEmail('');
    } catch (err) { console.error('Waitlist signup failed:', err); } finally { setSubmitting(false); }
  };

  return (
    <section id="coming-soon" data-testid="coming-soon-section" className="bg-[#062B52] relative grain-overlay py-20 md:py-28 px-6 md:px-12">
      <div className="max-w-4xl mx-auto relative z-10">
        <div className="text-center mb-12">
          <p className="eyebrow mb-4 text-[#BAE6FD]">Coming Soon</p>
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-white tracking-tight mb-4">Built the same way. Launching next.</h2>
          <p className="text-sm font-light text-white/50 max-w-lg mx-auto">The same pH-corrected, hard-water-safe science — extended to your scalp and body.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {[
            { icon: Scissors, title: 'Hair Care', desc: 'Sulphate-free scalp washes and conditioners at pH 4.8–5.5. Ketoconazole, Redensyl and Caffeine formulas for dandruff and hair fall.', testid: 'coming-soon-hair' },
            { icon: Droplets, title: 'Body Care', desc: 'pH-balanced body washes and syndet bars. Salicylic Acid and Niacinamide for body acne and dark spots.', testid: 'coming-soon-body' },
          ].map((c) => (
            <div key={c.title} data-testid={c.testid} className="p-8 rounded-[3px] relative" style={{ background: 'rgba(186,230,253,0.06)', border: '1px solid rgba(186,230,253,0.15)' }}>
              <span className="absolute top-4 right-4 text-[8px] font-sans font-semibold uppercase tracking-[2px] px-2.5 py-1 rounded-[2px] bg-[#BAE6FD] text-[#062B52]">Coming Soon</span>
              <div className="w-11 h-11 rounded-full flex items-center justify-center mb-5" style={{ background: 'rgba(186,230,253,0.12)' }}>
                <c.icon size={18} className="text-[#BAE6FD]" />
              </div>
              <h3 className="font-serif text-[24px] text-white font-semibold mb-2">{c.title}</h3>
              <p className="text-[13px] text-white/50 font-light leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-[13px] text-white/60 font-sans mb-4">Be first to know when they launch.</p>
          {done ? (
            <p data-testid="coming-soon-success" className="text-[14px] text-[#BAE6FD] font-sans">You're on the list. We'll email you at launch.</p>
          ) : (
            <form onSubmit={submit} data-testid="coming-soon-form" className="flex max-w-md mx-auto">
              <input
                data-testid="coming-soon-email"
                type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email" required
                className="flex-1 bg-white/5 border border-white/15 text-white text-[13px] px-4 py-3.5 rounded-l-[2px] focus:outline-none focus:border-[#BAE6FD] placeholder-white/30 font-sans"
              />
              <button data-testid="coming-soon-submit" type="submit" disabled={submitting}
                className="bg-[#BAE6FD] text-[#062B52] px-6 py-3.5 rounded-r-[2px] text-[11px] font-sans font-semibold uppercase tracking-[2px] hover:bg-white transition-colors disabled:opacity-50">
                {submitting ? '...' : 'Notify Me'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

function ProductCard({ product: p, variant, onAdd }) {
  const isPro = variant === 'pro';
  const goodFor = GOOD_FOR[p.slug] || [];
  const rating = RATINGS[p.slug] || { score: isPro ? 4.8 : 4.6, count: 500 };
  const actives = ACTIVES[p.slug];

  return (
    <div data-testid={`product-card-${p.slug}`} className="rounded-[3px] overflow-hidden group hover:-translate-y-0.5 transition-all duration-300"
      style={{ border: isPro ? '1px solid #0C7BC0' : '1px solid rgba(12,74,140,0.1)', boxShadow: '0 1px 3px rgba(12,74,140,0.04)' }}>
      <Link to={`/product/${p.slug}`} className="block">
        {p.image && (
          <div className="h-44 overflow-hidden bg-[#F0F9FF] relative" data-testid={`product-image-${p.slug}`}>
            <img src={p.image} alt={p.name} className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500" />
            {isPro && (
              <span className="absolute top-3 right-3 text-[7px] font-sans font-semibold uppercase tracking-[2px] px-2.5 py-1 rounded-[2px] bg-[#0C7BC0] text-white">PRO</span>
            )}
          </div>
        )}

        <div className="p-5 flex flex-col gap-1.5 bg-white"
          style={{ borderLeft: isPro ? '3px solid #0C7BC0' : '3px solid #0C4A8C' }}>
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-sans uppercase tracking-[2px] font-semibold text-[#0C7BC0]">
              {isPro ? 'TRUFORM Pro' : 'TRUFORM Daily'}
            </p>
            <div className="flex items-center gap-1" data-testid={`rating-${p.slug}`}>
              <div className="flex items-center gap-0.5">
                {[1,2,3,4,5].map(s => (
                  <Star key={s} size={10} className={s <= Math.round(rating.score) ? 'text-[#0C7BC0] fill-[#0C7BC0]' : 'text-[#CBD5E1]'} />
                ))}
              </div>
              <span className="text-[10px] font-sans text-[#0C4A8C] font-medium">{rating.score}</span>
              <span className="text-[9px] font-sans text-[#6B7D8A]">({rating.count.toLocaleString()})</span>
            </div>
          </div>

          <h3 className="font-serif text-[20px] font-semibold leading-tight text-[#0C4A8C]">
            {p.name.replace('TRUFORM ', '')}
          </h3>

          <span className="text-[13px] font-sans text-[#6B7D8A]">&#8377;{p.price} / {p.size}</span>

          {actives && <p className="text-[10px] font-sans text-[#0C7BC0] font-medium" data-testid={`actives-${p.slug}`}>{actives}</p>}

          {/* Proof badges: pH + Sulphate-free / No white cast */}
          <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`proof-badges-${p.slug}`}>
            {p.ph && <span className="text-[8px] font-sans font-semibold text-[#0C4A8C] bg-[#BAE6FD]/50 px-2 py-0.5 rounded-[2px]">pH {p.ph}</span>}
            {p.slug.includes('face-wash') && <span className="text-[8px] font-sans text-[#0C4A8C] bg-[#F0F9FF] px-2 py-0.5 rounded-[2px] border border-[#BAE6FD]">Sulphate-free</span>}
            {p.slug.includes('moisturiser') && <span className="text-[8px] font-sans text-[#0C4A8C] bg-[#F0F9FF] px-2 py-0.5 rounded-[2px] border border-[#BAE6FD]">No white cast</span>}
          </div>

          {goodFor.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1" data-testid={`good-for-${p.slug}`}>
              <span className="text-[8px] font-sans text-[#6B7D8A] uppercase tracking-[1px] self-center mr-0.5">Good for:</span>
              {goodFor.map((g) => (
                <span key={g} className="text-[8px] font-sans text-[#0C4A8C] bg-[#F0F9FF] px-2 py-0.5 rounded-[2px]">{g}</span>
              ))}
            </div>
          )}
        </div>
      </Link>

      <div className="bg-white px-5 py-3 flex items-center justify-between" style={{ borderTop: '1px solid rgba(12,74,140,0.06)' }}>
        <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
          {(p.tags || []).slice(0, 2).map((t) => (
            <span key={t} className="text-[7px] font-sans uppercase tracking-[1.5px] text-[#0C7BC0] bg-[#0C4A8C]/[0.04] px-2 py-0.5 rounded-[2px]">{t}</span>
          ))}
        </div>
        <button data-testid={`add-to-cart-${p.slug}`}
          onClick={(e) => { e.preventDefault(); onAdd({ id: p.slug, name: p.name, price: p.price, size: p.size, image: p.image || '', quantity: 1 }); }}
          className="flex items-center gap-1.5 text-[9px] font-sans uppercase tracking-[2px] text-[#0C4A8C]/60 hover:text-[#0C4A8C] font-medium ml-3 flex-shrink-0 transition-colors">
          <ShoppingBag size={12} /> Add
        </button>
      </div>
    </div>
  );
}
